/**
 * Event tile — the buyer-facing poster card, extracted out of
 * event-list.view.ts's admin grid so every surface listing events renders
 * the exact same visual rather than a hand-rolled stand-in that could
 * silently drift from it. Three surfaces mount it: event-list.view.ts's
 * admin events grid, event-form.view.ts's live preview pane (inline beside
 * the form, and in the narrow-viewport overlay), and
 * admin/checkin/events.view.ts's door-staff landing list. Purely
 * presentational: the row is assembled upstream (eventsPageView for the
 * admin grid, a form's own live draft for the preview, the check-in page's
 * own view-model for the door list) and passed in whole. A click dispatches
 * `open` with the row's id; a non-interactive mount (the preview pane) sets
 * `interactive=false` and gets neither the click affordance nor the
 * dispatch.
 *
 * Suppressing the schedule and venue lines is the mounting surface's own
 * claim, made through `posterCarriesSchedule` — the presence of an <img> is
 * no evidence that the artwork prints a date or a place, and this component
 * cannot read one. Only the admin grid makes the claim, because it shows
 * the artwork large enough that repeating a printed date and venue beside
 * it is noise. Every other mount says nothing and keeps both lines: the
 * form's preview must reflect the Schedule and Venue sections the organizer
 * is typing into, and the door list fills no footer, so those two lines are
 * all a staffer has to tell two same-artwork nights apart. Even where the
 * claim is made, a poster-less row keeps both, since nothing else on the
 * card then identifies when or where the event is.
 *
 * The `footer` slot is this card's one supplementary-data extension point —
 * event-list.view.ts fills it with the event's own sold/capacity figures;
 * event-form.view.ts's preview pane (a draft with no Firestore id) leaves it
 * unfilled. An empty named slot with no fallback content renders nothing
 * (the UA default `display: contents` on `<slot>` drops out of layout
 * entirely when it has no assigned nodes), so the preview never grows a gap
 * or an empty box for data it can't have yet.
 *
 * `density="row"` is the same identity at list scale, for a narrow phone
 * where two poster columns cut every name to one line: a 2:3 thumbnail
 * beside the name (two lines), date and venue. The thumbnail is too small
 * to carry a printed date, so the row ignores `posterCarriesSchedule`.
 *
 * `actionLabel` is the act the consumer's click performs ("Scan tickets for
 * …"); given one, it is the card's accessible name in place of the admin's
 * default "{name}, {status}, open event details".
 */
import { curtain } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { Event } from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import type { EventTileRow } from "./events.state.ts";

@schmancyElement("event-tile")
export class EventTile extends LocalizeMixin(SchmancyElement) {
	@property({ type: Object }) row!: EventTileRow;
	@property({ type: Boolean }) interactive = true;
	@property({ type: Boolean }) posterCarriesSchedule = false;
	@property({ type: String }) density: "poster" | "row" = "poster";
	@property({ type: String }) actionLabel = "";

	private open(): void {
		if (!this.interactive) return;
		this.dispatchEvent(
			new CustomEvent("open", {
				detail: { id: this.row.id },
				bubbles: true,
				composed: true,
			}),
		);
	}

	override render(): TemplateResult {
		const r = this.row;
		const status = this.t(`admin.common.status.${r.status}`);
		return html`
			<schmancy-card
				type=${r.status === "draft" ? "outlined" : "elevated"}
				?interactive=${this.interactive}
				class="group overflow-hidden rounded-2xl"
				aria-label=${
					this.interactive && this.actionLabel
						? this.actionLabel
						: this.interactive
							? this.t("admin.events.tile.ariaLabelInteractive", {
									name: r.name,
									status,
								})
							: this.t("admin.events.tile.ariaLabel", {
									name: r.name,
									status,
								})
				}
				@click=${() => this.open()}
			>
				${when(
					this.density === "row",
					() => html`
						<div class="grid grid-cols-[4rem_1fr] items-center gap-3 p-2 pr-4">
							<div class="aspect-[2/3] w-16 overflow-hidden rounded-xl bg-surface-containerHigh">
								${
									r.poster
										? html`<img src=${r.poster} alt="" decoding="async" loading="lazy" class="h-full w-full object-cover" />`
										: html`<div class="grid h-full w-full place-items-center">
												<schmancy-icon class="text-2xl text-surface-onVariant">confirmation_number</schmancy-icon>
											</div>`
								}
							</div>
							<div class="grid min-w-0 gap-1">
								${when(
									r.status !== "published",
									() =>
										html`<schmancy-typography type="label" token="md" class="text-surface-onVariant">${status}</schmancy-typography>`,
								)}
								<schmancy-typography type="title" token="sm" maxLines="2">${r.name}</schmancy-typography>
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${r.startLabel}</schmancy-typography>
								${when(
									r.venue,
									() =>
										html`<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${r.venue}</schmancy-typography>`,
								)}
							</div>
						</div>
					`,
					() => html`
				<div class="flex flex-col gap-3">
					<div
						${curtain({ active: r.status === "archived" })}
						class="relative aspect-[2/3] w-full bg-surface-containerHigh"
					>
						${
							r.poster
								? html`<img
										src=${r.poster}
										alt=""
										width=${Event.POSTER_WIDTH}
										height=${Event.POSTER_HEIGHT}
										decoding="async"
										loading="lazy"
										class="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
									/>`
								: html`
										<div class="flex h-full w-full items-center justify-center">
											<schmancy-icon class="text-5xl text-surface-onVariant/40">
												confirmation_number
											</schmancy-icon>
										</div>
									`
						}

					</div>
					<div class="grid gap-2 px-3 pb-3">
${when(
	r.status !== "published",
	() => html`
								<schmancy-typography type="label" token="md" class="text-surface-onVariant">
									${status}
								</schmancy-typography>
							`,
)}
						<div class="grid gap-1">
							<schmancy-typography type="title" token="sm" class="truncate">${r.name}</schmancy-typography>
							${when(
								!(this.posterCarriesSchedule && r.poster),
								() => html`
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant truncate">
										${r.startLabel}
									</schmancy-typography>
									${when(
										r.venue,
										() => html`
											<schmancy-typography type="body" token="sm" class="text-surface-onVariant truncate">
												${r.venue}
											</schmancy-typography>
										`,
									)}
								`,
							)}
						</div>
						<slot name="footer"></slot>
					</div>
				</div>
					`,
				)}
			</schmancy-card>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-tile": EventTile;
	}
}
